<template>
  <div class="neuron-hero-stage" ref="stage">
    <canvas ref="canvas" class="neuron-hero-canvas" :class="{ 'is-ready': ready }" />
    <div class="neuron-hero-vignette" aria-hidden="true" />
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref } from "vue";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

// =============================================================================
// noise + rng
// =============================================================================

function mulberry32(seed) {
  let a = (seed | 0) || 1;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function valueNoise3D(x, y, z) {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = x - xi, yf = y - yi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const w = zf * zf * (3 - 2 * zf);
  const hash = (a, b, c) => {
    const k = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453;
    return k - Math.floor(k);
  };
  const c000 = hash(xi,     yi,     zi    );
  const c100 = hash(xi + 1, yi,     zi    );
  const c010 = hash(xi,     yi + 1, zi    );
  const c110 = hash(xi + 1, yi + 1, zi    );
  const c001 = hash(xi,     yi,     zi + 1);
  const c101 = hash(xi + 1, yi,     zi + 1);
  const c011 = hash(xi,     yi + 1, zi + 1);
  const c111 = hash(xi + 1, yi + 1, zi + 1);
  const x00 = c000 * (1 - u) + c100 * u;
  const x10 = c010 * (1 - u) + c110 * u;
  const x01 = c001 * (1 - u) + c101 * u;
  const x11 = c011 * (1 - u) + c111 * u;
  const y0  = x00  * (1 - v) + x10  * v;
  const y1  = x01  * (1 - v) + x11  * v;
  return y0 * (1 - w) + y1 * w;
}

function fbm(x, y, z, octaves = 3) {
  let total = 0, amp = 0.5, freq = 1;
  for (let i = 0; i < octaves; i++) {
    total += valueNoise3D(x * freq, y * freq, z * freq) * amp;
    amp *= 0.5;
    freq *= 2.0;
  }
  return total;
}

// =============================================================================
// procedural neuron tree
// =============================================================================

function generateNeuronTree(seed, count, primaryDir) {
  const rng = mulberry32(seed);
  const dir0 = primaryDir.clone().normalize();
  const root = {
    pos: new THREE.Vector3(0, 0, 0),
    radius: 0.95,
    depth: 0,
    dir: dir0,
  };
  const neurons = [root];
  const edges = [];

  let attempts = 0;
  const maxAttempts = count * 24;

  while (neurons.length < count && attempts++ < maxAttempts) {
    const idxRand = Math.pow(rng(), 0.5);
    const idx = Math.min(neurons.length - 1, Math.floor(idxRand * neurons.length));
    const parent = neurons[idx];
    if (parent.depth >= 6) continue;

    const baseDir = parent.dir.clone();
    const upRef = Math.abs(baseDir.y) < 0.95 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const right = new THREE.Vector3().crossVectors(baseDir, upRef).normalize();
    const localUp = new THREE.Vector3().crossVectors(right, baseDir).normalize();

    const azimuth = (rng() - 0.5) * Math.PI * 1.6;
    const elevation = (rng() - 0.5) * Math.PI * 0.95;
    const newDir = baseDir.clone()
      .applyAxisAngle(localUp, azimuth)
      .applyAxisAngle(right, elevation)
      .normalize();

    const distance = 2.6 + Math.pow(rng(), 0.5) * 4.4;
    const newPos = parent.pos.clone().add(newDir.clone().multiplyScalar(distance));

    const minClearance = 1.4;
    let collision = false;
    for (const n of neurons) {
      if (n.pos.distanceTo(newPos) < minClearance) { collision = true; break; }
    }
    if (collision) continue;

    const radius = Math.max(0.36, parent.radius * (0.82 + rng() * 0.18));
    neurons.push({ pos: newPos, radius, depth: parent.depth + 1, dir: newDir });
    edges.push({
      from: idx,
      to: neurons.length - 1,
      curveSeed: Math.floor(rng() * 1e9),
    });
  }

  // ---- free axons / dendrite trees -------------------------------------
  // Each neuron sprouts 1-3 trunks that fade-taper into nothing. Each trunk
  // recursively spawns child branches (2 independent slots per node, with
  // spawn probability 0.7 at depth 0, decreasing 0.1 per depth, max depth 3).
  // Direction biased AWAY from camera (+Z) so trunks don't pop through view.
  const MAX_DEPTH = 3; // levels beyond root → 4 generations total (0, 1, 2, 3)
  const freeBranches = [];

  function pushBranchTree(rootStart, rootDir, rootLength, rootStartR) {
    // Iterative DFS so we don't recurse JS stack-deep.
    const stack = [
      { start: rootStart.clone(), dir: rootDir.clone().normalize(),
        length: rootLength, startR: rootStartR, depth: 0 },
    ];
    while (stack.length > 0) {
      const b = stack.pop();
      const end = b.start.clone().addScaledVector(b.dir, b.length);

      // Generate the actual rendered curve NOW so child branches can spawn
      // on it (instead of on the invisible linear axis). Same curve gets
      // reused at render time — single source of truth.
      const curveSeed = Math.floor(rng() * 1e9);
      const curve = makeStochasticAxonCurve(b.start, end, curveSeed, 12);

      freeBranches.push({
        start: b.start.clone(),
        end,
        curve,
        startR: b.startR,
        depth: b.depth,
        curveSeed,
      });

      if (b.depth >= MAX_DEPTH) continue;
      const branchChance = 0.7 - b.depth * 0.1; // 0.7, 0.6, 0.5, 0.4

      for (let slot = 0; slot < 2; slot++) {
        if (rng() > branchChance) continue;

        // Sprout location along the parent's ACTUAL curve, not its linear axis.
        const t = 0.35 + rng() * 0.45;
        const tangent = curve.getTangent(t).normalize();

        // Size child as a fraction of parent's ACTUAL radius at this t (not
        // parent.startR). Child flared base = ~1.9 × childStartR — keeping
        // childStartR at 0.42–0.52 of parent.radiusAt(t) means the flared
        // base lands at 0.80–0.99 of parent's local thickness: visibly
        // smaller, but big enough to read as a real offshoot.
        const parentRadiusAtT = freeBranchRadiusAt(b.startR, t);
        const childStartR = parentRadiusAtT * (0.42 + rng() * 0.10);

        // Push start backward along the parent's tangent so the child's
        // flared base sits inside the parent volume rather than half-sticking
        // out past the centerline.
        const pushBack = childStartR * 1.6;
        const childStart = curve.getPoint(t).addScaledVector(tangent, -pushBack);

        // Child direction: perturb the local tangent inside ~40° cone.
        const upRef = Math.abs(tangent.y) < 0.95
          ? new THREE.Vector3(0, 1, 0)
          : new THREE.Vector3(1, 0, 0);
        const right = new THREE.Vector3().crossVectors(tangent, upRef).normalize();
        const localUp = new THREE.Vector3().crossVectors(right, tangent).normalize();
        const angle1 = (rng() - 0.5) * Math.PI * 0.7;
        const angle2 = (rng() - 0.5) * Math.PI * 0.4;
        const childDir = tangent.clone()
          .applyAxisAngle(localUp, angle1)
          .applyAxisAngle(right, angle2)
          .normalize();

        const childLength = b.length * (0.65 + rng() * 0.35) + pushBack;

        stack.push({
          start: childStart, dir: childDir,
          length: childLength, startR: childStartR,
          depth: b.depth + 1,
        });
      }
    }
  }

  neurons.forEach((n, i) => {
    if (rng() < 0.35) return;
    const numTrunks = 1 + Math.floor(rng() * 3); // 1, 2, or 3 trunks per neuron
    for (let j = 0; j < numTrunks; j++) {
      let dir, tries = 0;
      do {
        dir = new THREE.Vector3(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1).normalize();
        tries++;
      } while (dir.z > 0.35 && tries < 6);

      // Long reaching trunks — into the void, not little stubs.
      const trunkLength = 2.5 + Math.pow(rng(), 0.5) * 4.0; // 2.5 - 6.5
      const trunkStartR = n.radius * (0.16 + rng() * 0.06);
      const trunkStart = n.pos.clone().addScaledVector(dir, n.radius * 0.65);

      pushBranchTree(trunkStart, dir, trunkLength, trunkStartR);
    }
  });

  return { neurons, edges, freeBranches };
}

// =============================================================================
// axon curve — toned-down stochastic perturbation
// =============================================================================

function makeStochasticAxonCurve(start, end, seed, segments = 14) {
  const dir = end.clone().sub(start);
  const len = dir.length();
  dir.normalize();
  const upRef = Math.abs(dir.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const right = new THREE.Vector3().crossVectors(dir, upRef).normalize();
  const up = new THREE.Vector3().crossVectors(right, dir).normalize();

  const rng = mulberry32(seed);
  const points = [];
  // Toned down further: gentle low-amplitude bends only.
  const ampMax = Math.min(len * 0.07, 0.35);

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const base = start.clone().lerp(end, t);
    if (i > 0 && i < segments) {
      const window = Math.sin(t * Math.PI);
      // 2 octaves instead of 3 — smoother large-scale bends, no fine wobble.
      const n1 = fbm(t * 2.5 + seed * 0.001, 0, 0, 2) * 2 - 1;
      const n2 = fbm(0, t * 2.5 + seed * 0.001, 0, 2) * 2 - 1;
      const k1 = (rng() * 2 - 1) * 0.12;
      const k2 = (rng() * 2 - 1) * 0.12;
      base.addScaledVector(right, (n1 + k1) * window * ampMax);
      base.addScaledVector(up,    (n2 + k2) * window * ampMax);
    }
    points.push(base);
  }
  return new THREE.CatmullRomCurve3(points, false, "catmullrom", 0.4);
}

// =============================================================================
// tube + globular geometry
// =============================================================================

function makeTaperedTube(curve, radiusFn, tubularSegments, radialSegments) {
  const frames = curve.computeFrenetFrames(tubularSegments, false);
  // World-space length so we can scale UV.x by it and get consistent texture
  // density per length regardless of how long the tube is.
  const arcLength = curve.getLength();
  const TILE_LENGTH = 0.8; // each texture tile spans ~0.8 world units along the tube
  const uvScaleU = Math.max(1, arcLength / TILE_LENGTH);

  const positions = [];
  const normals = [];
  const uvs = [];
  const indices = [];
  const tmpN = new THREE.Vector3();
  const tmpNormal = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const h = 0.5 / tubularSegments; // finite-difference step in t

  for (let i = 0; i <= tubularSegments; i++) {
    const t = i / tubularSegments;
    const r = radiusFn(t);
    const point = curve.getPoint(t);
    const N = frames.normals[i];
    const B = frames.binormals[i];

    // Taper slope: how fast the radius changes per unit arc length. The true
    // surface normal tilts along the axis by this amount — ignoring it (pure
    // radial normals) mislights tapered/flared sections.
    const t0 = Math.max(0, t - h);
    const t1 = Math.min(1, t + h);
    const drdt = (radiusFn(t1) - radiusFn(t0)) / (t1 - t0);
    const dsdt = curve.getPoint(t0).distanceTo(curve.getPoint(t1)) / (t1 - t0);
    const drds = dsdt > 1e-6 ? drdt / dsdt : 0;
    tangent.copy(curve.getTangent(t)).normalize();

    for (let j = 0; j < radialSegments; j++) {
      const theta = (j / radialSegments) * Math.PI * 2;
      const sin = Math.sin(theta);
      const cos = -Math.cos(theta);
      tmpN.set(0, 0, 0).addScaledVector(N, cos).addScaledVector(B, sin).normalize();
      positions.push(
        point.x + tmpN.x * r,
        point.y + tmpN.y * r,
        point.z + tmpN.z * r
      );
      // Tilt the radial normal backward along the tangent by the taper slope.
      tmpNormal.copy(tmpN).addScaledVector(tangent, -drds).normalize();
      normals.push(tmpNormal.x, tmpNormal.y, tmpNormal.z);
      uvs.push(t * uvScaleU, j / radialSegments);
    }
  }

  for (let i = 0; i < tubularSegments; i++) {
    for (let j = 0; j < radialSegments; j++) {
      const a = i * radialSegments + j;
      const b = i * radialSegments + ((j + 1) % radialSegments);
      const c = (i + 1) * radialSegments + ((j + 1) % radialSegments);
      const d = (i + 1) * radialSegments + j;
      indices.push(a, b, d, b, c, d);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeBoundingSphere();
  return geo;
}

// Closed-form radius of a free branch at parameter t. Must mirror the radiusFn
// used at render time so child branches can size and place themselves to
// match the parent's actual visible thickness.
function freeBranchRadiusAt(startR, t) {
  const baseR = startR * Math.pow(1 - t, 0.4);
  const flareT = Math.max(0, 1 - t / 0.18);
  const flareBoost = startR * 0.9 * Math.pow(flareT, 2);
  return baseR + flareBoost;
}

function makeGlobularGeometry(radius, seedOffset) {
  const geo = new THREE.IcosahedronGeometry(radius, 4);
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const dir = v.clone().normalize();
    const n = fbm(
      dir.x * 1.4 + seedOffset,
      dir.y * 1.4 + seedOffset * 1.7,
      dir.z * 1.4 + seedOffset * 0.6,
      3
    );
    const scale = radius * (0.7 + n * 0.5);
    pos.setXYZ(i, dir.x * scale, dir.y * scale, dir.z * scale);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

// =============================================================================
// fresnel injection
// =============================================================================

function applyFresnelChunk(material, color, power, intensity) {
  material.userData.fresnelUniforms = {
    uFresnelPower: { value: power },
    uFresnelIntensity: { value: intensity },
    uFresnelColor: { value: color.clone() },
  };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFresnelPower = material.userData.fresnelUniforms.uFresnelPower;
    shader.uniforms.uFresnelIntensity = material.userData.fresnelUniforms.uFresnelIntensity;
    shader.uniforms.uFresnelColor = material.userData.fresnelUniforms.uFresnelColor;
    shader.fragmentShader =
      `uniform float uFresnelPower;
       uniform float uFresnelIntensity;
       uniform vec3 uFresnelColor;
      ` + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <opaque_fragment>",
      `
      #include <opaque_fragment>
      {
        vec3 viewDir = normalize(vViewPosition);
        vec3 nrm = normalize(vNormal);
        float fres = pow(1.0 - clamp(abs(dot(nrm, viewDir)), 0.0, 1.0), uFresnelPower);
        gl_FragColor.rgb += fres * uFresnelColor * uFresnelIntensity;
      }
      `
    );
  };
  material.needsUpdate = true;
  return material;
}

// Carve a world-space sphere out of a material. Fragments whose world
// position is within uCarveRadius of uCarveCenter are discarded — gives a
// "cross-section" effect when the mouse hovers a body and reveals the
// nucleus + halo + outline that sit inside it.
//
// Wraps any existing onBeforeCompile (so call this AFTER applyFresnelChunk).
function applyCarveChunk(material) {
  const prevCallback = material.onBeforeCompile;
  material.userData.carveUniforms = {
    uCarveCenter: { value: new THREE.Vector3(0, 0, 0) },
    uCarveRadius: { value: 0 },
  };
  material.onBeforeCompile = (shader) => {
    if (prevCallback) prevCallback(shader);
    shader.uniforms.uCarveCenter = material.userData.carveUniforms.uCarveCenter;
    shader.uniforms.uCarveRadius = material.userData.carveUniforms.uCarveRadius;

    shader.vertexShader = `varying vec3 vCarveWorldPos;
` + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      "#include <begin_vertex>",
      `#include <begin_vertex>
       vCarveWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`
    );

    shader.fragmentShader =
      `varying vec3 vCarveWorldPos;
       uniform vec3 uCarveCenter;
       uniform float uCarveRadius;
      ` + shader.fragmentShader;

    shader.fragmentShader = shader.fragmentShader.replace(
      "void main() {",
      `void main() {
         if (uCarveRadius > 0.001 && distance(vCarveWorldPos, uCarveCenter) < uCarveRadius) {
           discard;
         }
      `
    );
  };
  material.needsUpdate = true;
  return material;
}

// =============================================================================
// component
// =============================================================================

export default {
  name: "NeuronHero",
  props: {
    seed: { type: Number, default: 11 },
    count: { type: Number, default: 11 },
    orientation: {
      type: Array,
      default: () => [1, 0.18, 0.05],
    },
  },
  setup(props) {
    const stage = ref(null);
    const canvas = ref(null);
    const ready = ref(false);

    let renderer, scene, camera, composer, raf;
    let bloomPass, outlinePass;
    let pulseGroup, pulseGeo;
    let glowTex = null; // shared soft radial-gradient sprite texture
    let bodyMat = null; // exposed so tick can push carve uniforms
    let visible = true;
    let intersectionObserver = null;
    const disposables = [];
    const targetMouse = new THREE.Vector2(0, 0);
    const smoothMouse = new THREE.Vector2(0, 0);
    const ndc = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();

    // Carve effect state (smoothly animated → uniforms each frame)
    const targetCarveCenter = new THREE.Vector3();
    const smoothCarveCenter = new THREE.Vector3();
    let targetCarveRadius = 0;
    let smoothCarveRadius = 0;

    // Persistent state for pulses + raycasting
    const neuronBodies = [];      // body meshes (raycast targets)
    const nucleusMeshes = [];     // nucleus meshes (each has its own material clone)
    const haloMeshes = [];        // halo meshes (mirror nucleus flash)
    const axonRecords = [];       // [{ curve, length, fromIdx, toIdx }]
    const activePulses = [];      // [{ axonIdx, t, speed, dir, mesh, mat, targetIdx, intensity }]
    let pulseTimer = 0;
    let nextPulseDelay = 0.05;
    let lastTickTime = -1;

    // ---- cool blue palette --------------------------------------------------
    // Nucleus pushed toward saturated electric cyan for the bright glowing
    // center the user wants.
    const COLOR_NUCLEUS_HOT  = new THREE.Color("#5EE5FF"); // electric cyan
    const COLOR_NUCLEUS_DEEP = new THREE.Color("#2563EB"); // royal blue body
    const COLOR_HALO         = new THREE.Color("#6EE7FF");
    const COLOR_FRESNEL      = new THREE.Color("#A8DCFF");
    const COLOR_OUTLINE_VIS  = new THREE.Color("#7DD3FC");
    const COLOR_OUTLINE_HID  = new THREE.Color("#1E3A5F");
    const COLOR_PULSE        = new THREE.Color("#A8E3FF");

    function buildScene() {
      const group = new THREE.Group();

      const { neurons, edges, freeBranches } = generateNeuronTree(
        props.seed,
        props.count,
        new THREE.Vector3(...props.orientation)
      );

      // Textures — body uses 1×1 wrap, axons use 3×1 so the texture tiles
      // along the tube length and reads as continuous fibrous surface.
      const texLoader = new THREE.TextureLoader();
      // Reveal the canvas only once the surface texture has actually loaded, so
      // the scene doesn't pop from un-textured (flat/brighter) to textured a
      // second or two later.
      const diffuseTex = texLoader.load("/textures/neuron-tex.png", () => {
        ready.value = true;
      });
      diffuseTex.wrapS = diffuseTex.wrapT = THREE.RepeatWrapping;
      diffuseTex.anisotropy = 4;
      diffuseTex.colorSpace = THREE.SRGBColorSpace;

      const metalTex = texLoader.load("/textures/neuron-tex.png");
      metalTex.wrapS = metalTex.wrapT = THREE.RepeatWrapping;

      // Mirror on S so consecutive tiles flip and hide seams; UV scaling in
      // makeTaperedTube gives us per-axon tile density, no need for repeat here.
      const axonDiffuseTex = texLoader.load("/textures/neuron-tex.png");
      axonDiffuseTex.wrapS = THREE.MirroredRepeatWrapping;
      axonDiffuseTex.wrapT = THREE.RepeatWrapping;
      axonDiffuseTex.anisotropy = 8;
      axonDiffuseTex.colorSpace = THREE.SRGBColorSpace;

      const axonMetalTex = texLoader.load("/textures/neuron-tex.png");
      axonMetalTex.wrapS = THREE.MirroredRepeatWrapping;
      axonMetalTex.wrapT = THREE.RepeatWrapping;

      disposables.push(diffuseTex, metalTex, axonDiffuseTex, axonMetalTex);

      // Body material — shared across neurons (no per-neuron flashing on body)
      bodyMat = new THREE.MeshPhysicalMaterial({
        color: 0xC0D6E5,
        map: diffuseTex,
        metalnessMap: metalTex,
        metalness: 0.35,
        roughness: 0.5,
        clearcoat: 0.4,
        clearcoatRoughness: 0.28,
        transparent: false,
        opacity: 1,
      });
      applyFresnelChunk(bodyMat, COLOR_FRESNEL, 3.4, 0.6);
      // Add the world-space sphere carve on top of fresnel.
      applyCarveChunk(bodyMat);
      disposables.push(bodyMat);

      // Nucleus material is CLONED per neuron so we can flash one without
      // affecting the others (shared materials would broadcast the flash).
      const nucleusMatTemplate = new THREE.MeshStandardMaterial({
        color: COLOR_NUCLEUS_DEEP,
        emissive: COLOR_NUCLEUS_HOT,
        emissiveIntensity: 3.3,
        roughness: 0.35,
        metalness: 0.15,
      });
      disposables.push(nucleusMatTemplate);

      const axonMat = new THREE.MeshPhysicalMaterial({
        color: 0xC0D6E5,
        map: axonDiffuseTex,
        metalnessMap: axonMetalTex,
        // Match the body so axons read as part of the same surface, not a
        // different (brighter, less metallic) material on a different geometry.
        metalness: 0.35,
        roughness: 0.5,
        clearcoat: 0.4,
        clearcoatRoughness: 0.28,
        transparent: false,
        opacity: 1,
      });
      applyFresnelChunk(axonMat, COLOR_FRESNEL, 3.4, 0.5);
      disposables.push(axonMat);

      // ---- neurons ---------------------------------------------------------
      neurons.forEach((n, i) => {
        const bodyGeo = makeGlobularGeometry(n.radius, i * 7.31 + props.seed * 0.13);
        disposables.push(bodyGeo);
        const body = new THREE.Mesh(bodyGeo, bodyMat);
        body.position.copy(n.pos);
        body.userData.neuronIdx = i;
        body.userData.radius = n.radius; // for carve sizing
        body.userData.bob = Math.random() * Math.PI * 2;
        group.add(body);
        neuronBodies.push(body);

        const nucMat = nucleusMatTemplate.clone();
        nucMat.userData = { baseEmissive: 3.3 };
        disposables.push(nucMat);

        const nucGeo = makeGlobularGeometry(n.radius * 0.34, i * 11.71 + 1000);
        disposables.push(nucGeo);
        const nucleus = new THREE.Mesh(nucGeo, nucMat);
        nucleus.position.copy(n.pos);
        nucleus.userData = {
          basePos: n.pos.clone(),
          pulsePhase: Math.random() * Math.PI * 2,
          flashLevel: 0,
        };
        group.add(nucleus);
        nucleusMeshes.push(nucleus);

        // Soft glow sprite around the neuron — radial gradient that fades to
        // transparent at the edges (no hard sphere edge).
        const haloMat = new THREE.SpriteMaterial({
          map: glowTex,
          color: COLOR_HALO,
          transparent: true,
          opacity: 0.3,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        haloMat.userData = { baseOpacity: 0.3 };
        disposables.push(haloMat);
        const halo = new THREE.Sprite(haloMat);
        halo.scale.setScalar(n.radius * 3.4);
        halo.position.copy(n.pos);
        group.add(halo);
        haloMeshes.push(halo);
      });

      // ---- axons -----------------------------------------------------------
      edges.forEach((e) => {
        const a = neurons[e.from];
        const b = neurons[e.to];
        const dir = b.pos.clone().sub(a.pos).normalize();
        // Endpoints sit DEEP inside each neuron (was 0.92 of the surface).
        // Globular bodies have noise-displaced surface in [0.7r, 1.2r], so
        // 0.65r is well below the minimum surface — the tube emerges through
        // the body and visually merges with it.
        const start = a.pos.clone().addScaledVector(dir, a.radius * 0.65);
        const end = b.pos.clone().addScaledVector(dir, -b.radius * 0.65);

        const curve = makeStochasticAxonCurve(start, end, e.curveSeed, 14);

        // Stringy / hair-thin axons that flare at each soma — the trumpet
        // shape lets the tube merge into the body instead of butting against
        // it with a hard circular edge.
        const startR = a.radius * 0.16;
        const endR = b.radius * 0.16;
        const minR = Math.min(startR, endR) * 0.85;
        const flareEnd = Math.min(startR, endR) * 0.9; // additive boost magnitude
        const radiusFn = (t) => {
          const w = 4 * t * (1 - t);
          const k = w * w;
          const lerpedSurface = THREE.MathUtils.lerp(startR, endR, t);
          const baseR = THREE.MathUtils.lerp(lerpedSurface, minR, k);
          // Trumpet flare at BOTH ends (t→0 and t→1).
          const flareT = 2 * Math.abs(t - 0.5); // 0 in middle, 1 at endpoints
          const flareBoost = flareEnd * Math.pow(flareT, 6); // narrow ramp
          const jitter = (fbm(t * 6 + e.curveSeed * 0.0017, 0, 0, 2) * 2 - 1) * 0.05;
          return (baseR + flareBoost) * (1 + jitter);
        };

        const length = curve.getLength();
        const tubularSegments = Math.max(48, Math.floor(length * 18));
        const geo = makeTaperedTube(curve, radiusFn, tubularSegments, 12);
        disposables.push(geo);
        const mesh = new THREE.Mesh(geo, axonMat);
        group.add(mesh);

        axonRecords.push({
          curve, // CatmullRomCurve3 is in world space (built from neuron positions)
          length,
          fromIdx: e.from,
          toIdx: e.to,
        });
      });

      // ---- free branch trees (taper to nothing, recursively branched) ----
      // Each entry is one tube. The tree topology is built into the spatial
      // arrangement: child branch starts on its parent's centerline mid-length,
      // emerges in a perturbed direction, with thinner start radius.
      freeBranches.forEach((br) => {
        // Same curve that was used to spawn child branches — guarantees
        // the visible tube and the children's roots actually agree.
        const curve = br.curve;

        const flareRoot = br.startR * 0.9;
        const radiusFn = (t) => {
          // (1-t)^0.4 keeps the base thick most of the way; sharp pinch at tip.
          const baseR = br.startR * Math.pow(1 - t, 0.4);
          // One-sided flare at the root only (where the branch meets its
          // neuron or its parent branch). Drops to 0 by t≈0.18.
          const flareT = Math.max(0, 1 - t / 0.18);
          const flareBoost = flareRoot * Math.pow(flareT, 2);
          const jitter = (fbm(t * 6 + br.curveSeed * 0.0019, 0, 0, 2) * 2 - 1) * 0.05;
          return (baseR + flareBoost) * (1 + jitter);
        };

        const length = curve.getLength();
        const tubularSegments = Math.max(32, Math.floor(length * 16));
        // Child branches are thin → fewer radial segments, save geometry.
        const radial = br.depth === 0 ? 10 : 8;
        const geo = makeTaperedTube(curve, radiusFn, tubularSegments, radial);
        disposables.push(geo);
        const mesh = new THREE.Mesh(geo, axonMat);
        group.add(mesh);
      });

      // Center the bbox so all positions also offset.
      const bbox = new THREE.Box3().setFromObject(group);
      const center = bbox.getCenter(new THREE.Vector3());
      group.position.sub(center);

      // Store the offset so pulses can place themselves in the same group space.
      group.userData.centerOffset = center.clone().negate();

      return group;
    }

    function makeEnvTexture() {
      const c = document.createElement("canvas");
      c.width = 1024; c.height = 512;
      const ctx = c.getContext("2d");
      const grad = ctx.createLinearGradient(0, 0, 0, 512);
      grad.addColorStop(0.0, "#DCE7F1");
      grad.addColorStop(0.4, "#CcD9E8");
      grad.addColorStop(0.7, "#B9CCE0");
      grad.addColorStop(1.0, "#9FB6CF");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1024, 512);
      ctx.fillStyle = "rgba(230,240,250,0.45)";
      ctx.fillRect(0, 80, 1024, 30);
      ctx.fillStyle = "rgba(125, 211, 252, 0.10)";
      ctx.fillRect(0, 230, 1024, 8);
      const tex = new THREE.CanvasTexture(c);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    }

    function setupLights(s) {
      s.add(new THREE.AmbientLight(0xffffff, 0.25));
      const key = new THREE.DirectionalLight(0xffffff, 0.7);
      key.position.set(4, 5, 6);
      s.add(key);
      const rim = new THREE.DirectionalLight(0x7da8d8, 0.45);
      rim.position.set(-4, -2, -3);
      s.add(rim);
    }

    // ---- pulse system ------------------------------------------------------

    // Soft radial-gradient sprite — opaque center fading smoothly to fully
    // transparent at the edge. Used for the neuron halo and the pulse glow so
    // they bleed out softly instead of showing a hard sphere edge.
    function makeGlowTexture() {
      const c = document.createElement("canvas");
      c.width = c.height = 128;
      const ctx = c.getContext("2d");
      const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      g.addColorStop(0.0, "rgba(255,255,255,1)");
      g.addColorStop(0.25, "rgba(255,255,255,0.85)");
      g.addColorStop(0.55, "rgba(255,255,255,0.35)");
      g.addColorStop(1.0, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 128, 128);
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      return tex;
    }

    function makePulseMaterial() {
      return new THREE.MeshBasicMaterial({
        color: COLOR_PULSE,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
    }

    // Big soft glow sprite that rides along with a pulse.
    function makePulseGlow() {
      const mat = new THREE.SpriteMaterial({
        map: glowTex,
        color: COLOR_PULSE,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const sprite = new THREE.Sprite(mat);
      sprite.scale.setScalar(1.6);
      return sprite;
    }

    function spawnPulse(axonIdx, direction = 1, intensity = 1) {
      const rec = axonRecords[axonIdx];
      if (!rec) return;
      const mat = makePulseMaterial();
      const mesh = new THREE.Mesh(pulseGeo, mat);
      pulseGroup.add(mesh);
      // Large soft glow that travels with the pulse.
      const glow = makePulseGlow();
      pulseGroup.add(glow);
      activePulses.push({
        axonIdx,
        t: direction > 0 ? 0 : 1,
        // Speed in t-units per second. Smaller value = slower travel.
        speed: 0.55 + Math.random() * 0.4,
        dir: direction,
        mesh,
        mat,
        glow,
        intensity,
        targetIdx: direction > 0 ? rec.toIdx : rec.fromIdx,
      });
    }

    function flashNeuron(idx, peak) {
      if (idx < 0 || idx >= nucleusMeshes.length) return;
      const ud = nucleusMeshes[idx].userData;
      ud.flashLevel = Math.max(ud.flashLevel || 0, peak);
    }

    function spawnPulsesFromNeuron(idx, intensity = 1.4) {
      axonRecords.forEach((rec, axonIdx) => {
        if (rec.fromIdx === idx) spawnPulse(axonIdx, +1, intensity);
        else if (rec.toIdx === idx) spawnPulse(axonIdx, -1, intensity);
      });
    }

    function updatePulses(dt) {
      for (let i = activePulses.length - 1; i >= 0; i--) {
        const p = activePulses[i];
        p.t += dt * p.speed * p.dir;
        const arrived =
          (p.dir > 0 && p.t >= 1) ||
          (p.dir < 0 && p.t <= 0);
        if (arrived) {
          flashNeuron(p.targetIdx, 3.2 * p.intensity);
          pulseGroup.remove(p.mesh);
          pulseGroup.remove(p.glow);
          p.mat.dispose();
          p.glow.material.dispose();
          activePulses.splice(i, 1);
          continue;
        }
        const tc = THREE.MathUtils.clamp(p.t, 0, 1);
        const point = axonRecords[p.axonIdx].curve.getPointAt(tc);
        // Pulses live as children of `tree`, which already has the bbox
        // centering offset on its transform — setting the local position to
        // the world-space curve point lets that transform do the work once.
        p.mesh.position.copy(point);
        const fade = Math.sin(tc * Math.PI);
        p.mat.opacity = (0.25 + fade * 0.7) * p.intensity;
        p.mesh.scale.setScalar((0.5 + fade * 0.7) * Math.min(p.intensity, 1.4));
        // Large soft glow follows the pulse, fading in/out along the path.
        p.glow.position.copy(point);
        p.glow.material.opacity = (0.22 + fade * 0.38) * p.intensity;
        p.glow.scale.setScalar((1.1 + fade * 1.1) * Math.min(p.intensity, 1.4));
      }
    }

    function updateNucleusFlashes(dt) {
      for (let i = 0; i < nucleusMeshes.length; i++) {
        const ud = nucleusMeshes[i].userData;
        ud.flashLevel = Math.max(0, (ud.flashLevel || 0) - dt * 3.2);
        const mat = nucleusMeshes[i].material;
        mat.emissiveIntensity = (mat.userData.baseEmissive || 3.4) + ud.flashLevel * 1.6;
        // Mirror flash on halo opacity so it pops too
        const halo = haloMeshes[i];
        if (halo) {
          halo.material.opacity =
            (halo.material.userData.baseOpacity || 0.45) + ud.flashLevel * 0.22;
        }
      }
    }

    // ---- input handlers ----------------------------------------------------

    function setNDC(ev) {
      const rect = canvas.value.getBoundingClientRect();
      ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
    }

    function onPointerMove(ev) {
      setNDC(ev);
      const rect = canvas.value.getBoundingClientRect();
      const x = (ev.clientX - rect.left) / rect.width;
      const y = (ev.clientY - rect.top) / rect.height;
      targetMouse.x = x * 2 - 1;
      targetMouse.y = -(y * 2 - 1);

      // Hover: cursor feedback + carve target.
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(neuronBodies, false);
      if (hits.length > 0) {
        const obj = hits[0].object;
        // Center the carve on the actual surface hit point (already world space).
        targetCarveCenter.copy(hits[0].point);
        targetCarveRadius = (obj.userData.radius || 0.6) * 0.7;
        canvas.value.style.cursor = "pointer";
      } else {
        targetCarveRadius = 0;
        canvas.value.style.cursor = "default";
      }
    }
    function onPointerLeave() {
      targetMouse.set(0, 0);
      targetCarveRadius = 0;
      if (canvas.value) canvas.value.style.cursor = "default";
    }

    function onClick(ev) {
      setNDC(ev);
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(neuronBodies, false);
      if (hits.length > 0) {
        const idx = hits[0].object.userData.neuronIdx;
        if (idx !== undefined) {
          flashNeuron(idx, 4.0);
          spawnPulsesFromNeuron(idx, 1.4);
        }
      }
    }

    // ---- frame loop --------------------------------------------------------

    function tick(t) {
      raf = requestAnimationFrame(tick);
      // Skip everything when offscreen — saves the bloom + outline + render
      // passes when the user is looking at any other section.
      if (!visible) return;
      const time = t * 0.001;
      const dt = lastTickTime < 0 ? 0.016 : Math.min(time - lastTickTime, 0.05);
      lastTickTime = time;

      smoothMouse.x += (targetMouse.x - smoothMouse.x) * 0.05;
      smoothMouse.y += (targetMouse.y - smoothMouse.y) * 0.05;

      const orbit = time * 0.06;
      camera.position.x = Math.sin(orbit) * 0.9 + smoothMouse.x * 0.6;
      camera.position.y = 0.6 + Math.sin(time * 0.04) * 0.25 + smoothMouse.y * 0.4;
      camera.position.z = 10.5;
      camera.lookAt(-1.6, -0.3, 0);

      // Random pulse spawns.
      pulseTimer += dt;
      if (pulseTimer >= nextPulseDelay && axonRecords.length > 0) {
        pulseTimer = 0;
        nextPulseDelay = 0.25 + Math.random() * 1.0;
        const axonIdx = Math.floor(Math.random() * axonRecords.length);
        const direction = Math.random() < 0.5 ? +1 : -1;
        spawnPulse(axonIdx, direction, 1.0);
      }

      updatePulses(dt);
      updateNucleusFlashes(dt);

      // Smooth carve animation → push to body material uniforms.
      smoothCarveRadius += (targetCarveRadius - smoothCarveRadius) * 0.22;
      smoothCarveCenter.lerp(targetCarveCenter, 0.28);
      if (bodyMat?.userData?.carveUniforms) {
        bodyMat.userData.carveUniforms.uCarveCenter.value.copy(smoothCarveCenter);
        bodyMat.userData.carveUniforms.uCarveRadius.value = smoothCarveRadius;
      }

      // Subtle nucleus breathing on top of flash level.
      for (let i = 0; i < nucleusMeshes.length; i++) {
        const ud = nucleusMeshes[i].userData;
        if (ud?.pulsePhase !== undefined) {
          const p = 0.94 + 0.10 * Math.sin(time * 1.6 + ud.pulsePhase);
          nucleusMeshes[i].scale.setScalar(p);
        }
      }

      composer.render();
    }

    function resize() {
      if (!stage.value || !renderer) return;
      const w = stage.value.clientWidth;
      const h = stage.value.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      renderer.setPixelRatio(dpr);
      renderer.setSize(w, h, false);
      composer.setSize(w, h);
      bloomPass.setSize(w, h);
      outlinePass.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    onMounted(() => {
      scene = new THREE.Scene();
      // Soft cool blue-grey backdrop (not pure white) so the blue neurons read
      // with contrast instead of washing out.
      scene.background = new THREE.Color(0xDCE7F2);
      scene.environment = makeEnvTexture();
      scene.environmentIntensity = 0.4;

      camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
      camera.position.set(0, 0.6, 10.5);
      camera.lookAt(-1.6, -0.3, 0);

      renderer = new THREE.WebGLRenderer({
        canvas: canvas.value,
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
      renderer.setClearColor(0xDCE7F2, 1);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.85;

      setupLights(scene);

      glowTex = makeGlowTexture();
      disposables.push(glowTex);

      const tree = buildScene();
      scene.add(tree);

      // Pulse group lives next to the tree so getPointAt's curve coords
      // (built before re-centering) get offset into the same space.
      pulseGroup = new THREE.Group();
      tree.add(pulseGroup);

      pulseGeo = new THREE.SphereGeometry(0.09, 16, 12);
      disposables.push(pulseGeo);

      const renderPass = new RenderPass(scene, camera);

      outlinePass = new OutlinePass(new THREE.Vector2(1, 1), scene, camera);
      outlinePass.edgeStrength = 3.5;     // was 5.5
      outlinePass.edgeGlow = 0.7;         // was 1.4
      outlinePass.edgeThickness = 1.2;    // was 1.4
      outlinePass.pulsePeriod = 4.0;      // gentler breath
      outlinePass.visibleEdgeColor.copy(COLOR_OUTLINE_VIS);
      outlinePass.hiddenEdgeColor.copy(COLOR_OUTLINE_HID);
      outlinePass.selectedObjects = nucleusMeshes;

      bloomPass = new UnrealBloomPass(
        new THREE.Vector2(1, 1),
        0.22, // strength — keep it gentle so the whole-frame glow is soft, not blown out
        0.6,  // radius
        0.55  // threshold — BELOW the background brightness on purpose, so the
              // whole frame blooms into that soft white glow/haze (the wanted
              // effect) — just dialed down via the low strength above.
      );

      const outputPass = new OutputPass();

      composer = new EffectComposer(renderer);
      composer.addPass(renderPass);
      composer.addPass(outlinePass);
      composer.addPass(bloomPass);
      composer.addPass(outputPass);

      resize();
      window.addEventListener("resize", resize);
      canvas.value.addEventListener("pointermove", onPointerMove);
      canvas.value.addEventListener("pointerleave", onPointerLeave);
      canvas.value.addEventListener("click", onClick);

      if (typeof IntersectionObserver !== "undefined" && stage.value) {
        intersectionObserver = new IntersectionObserver(
          (entries) => { visible = entries[0]?.isIntersecting ?? true; },
          { threshold: 0 }
        );
        intersectionObserver.observe(stage.value);
      }

      raf = requestAnimationFrame(tick);
    });

    onBeforeUnmount(() => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (canvas.value) {
        canvas.value.removeEventListener("pointermove", onPointerMove);
        canvas.value.removeEventListener("pointerleave", onPointerLeave);
        canvas.value.removeEventListener("click", onClick);
      }
      activePulses.forEach((p) => {
        p.mat?.dispose?.();
        p.glow?.material?.dispose?.();
      });
      activePulses.length = 0;
      intersectionObserver?.disconnect?.();
      disposables.forEach((d) => d.dispose && d.dispose());
      composer?.dispose?.();
      renderer?.dispose?.();
    });

    return { stage, canvas, ready };
  },
};
</script>

<style scoped>
.neuron-hero-stage {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 480px;
}
.neuron-hero-canvas {
  display: block;
  width: 100%;
  height: 100%;
  background: transparent;
  /* Hidden until the surface texture loads, then fade in — avoids the
     un-textured → textured "pop" a couple seconds after mount. */
  opacity: 0;
  transition: opacity 600ms ease;
}
.neuron-hero-canvas.is-ready {
  opacity: 1;
}
.neuron-hero-vignette {
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 65% 50%,
    transparent 0%,
    transparent 72%,
    rgba(255, 255, 255, 0.22) 100%
  );
}
</style>
